/**
 * Dashboard: artículos recientes, destacados, accesos rápidos y búsqueda global.
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Clock, Star, FileText, BookOpen, Sparkles, ArrowRight, Plus, Loader2 } from 'lucide-react';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useCreateSection,
  useFeaturedArticles,
  useNavTree,
  useRecentArticles,
} from '@/features/content/queries';
import { useAuth } from '@/features/auth/AuthProvider';
import { timeAgo } from '@/lib/utils';

function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const recent = useRecentArticles(6);
  const featured = useFeaturedArticles();
  const tree = useNavTree();
  const createSection = useCreateSection();
  const isAdmin = user?.rol === 'admin';
  const [newSectionOpen, setNewSectionOpen] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');

  const createSec = async () => {
    if (!newSectionTitle.trim()) return;
    const res = await createSection.mutateAsync({ titulo: newSectionTitle.trim() });
    setNewSectionOpen(false);
    setNewSectionTitle('');
    navigate({ to: '/seccion/$section', params: { section: res.section.slug } });
  };

  return (
    <div className="space-y-10">
      <header className="flex items-end justify-between gap-4">
        <div className="space-y-1">
          <p className="text-muted-foreground text-sm">¡Hola, {user?.nombre?.split(' ')[0]} 👋</p>
          <h1 className="text-3xl font-bold tracking-tight">Base de Conocimiento</h1>
          <p className="text-muted-foreground max-w-2xl text-sm">
            Encuentra procedimientos, manuales, políticas y contactos internos de Repuestos España.
          </p>
        </div>
        {isAdmin && (
          <Button variant="brand" onClick={() => setNewSectionOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            Nueva sección
          </Button>
        )}
      </header>

      {/* Accesos rápidos */}
      <section>
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <Sparkles className="h-4 w-4" />
          Accesos rápidos
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {tree.isLoading
            ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)
            : (tree.data?.sections ?? []).slice(0, 4).map((s) => (
                <Link
                  key={s.id}
                  to="/seccion/$section"
                  params={{ section: s.slug }}
                  className="group bg-card hover:border-brand/40 hover:bg-accent/40 rounded-xl border p-4 transition-all"
                >
                  <BookOpen className="text-brand mb-2 h-5 w-5" />
                  <div className="text-sm font-semibold">{s.titulo}</div>
                  <div className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                    {s.descripcion ?? `${s.subsections.length} subsecciones`}
                  </div>
                </Link>
              ))}
        </div>
      </section>

      {/* Destacados */}
      <section>
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <Star className="h-4 w-4" /> Destacados
        </h2>
        {featured.isLoading ? (
          <Skeleton className="h-24 w-full rounded-xl" />
        ) : (featured.data?.articles ?? []).length === 0 ? (
          <p className="text-muted-foreground text-sm">Aún no hay artículos destacados.</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {featured.data!.articles.map((a) => (
              <Link
                key={a.id}
                to="/seccion/$section/$subsection/$article"
                params={{
                  section: a.sectionSlug,
                  subsection: a.subsectionSlug,
                  article: a.slug,
                }}
              >
                <Card className="hover:border-brand/40 group transition-colors">
                  <CardHeader>
                    <Badge variant="brand" className="w-fit">{a.sectionTitulo}</Badge>
                    <CardTitle className="group-hover:text-brand mt-2 text-base transition-colors">
                      {a.titulo}
                    </CardTitle>
                    {a.resumen && <CardDescription>{a.resumen}</CardDescription>}
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Recientes */}
      <section>
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <Clock className="h-4 w-4" /> Actualizado recientemente
        </h2>
        {recent.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : (recent.data?.articles ?? []).length === 0 ? (
          <p className="text-muted-foreground text-sm">No hay artículos aún.</p>
        ) : (
          <ul className="bg-card divide-y rounded-xl border">
            {recent.data!.articles.map((a) => (
              <li key={a.id}>
                <Link
                  to="/seccion/$section/$subsection/$article"
                  params={{
                    section: a.sectionSlug,
                    subsection: a.subsectionSlug,
                    article: a.slug,
                  }}
                  className="group hover:bg-accent/40 flex items-center gap-3 px-4 py-3 transition-colors"
                >
                  <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{a.titulo}</div>
                    <div className="text-muted-foreground text-xs">
                      {a.sectionTitulo} · {timeAgo(a.updatedAt)}
                      {a.autorNombre && ` · por ${a.autorNombre}`}
                    </div>
                  </div>
                  <ArrowRight className="text-muted-foreground h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog open={newSectionOpen} onOpenChange={setNewSectionOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nueva sección</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Título</label>
            <Input
              autoFocus
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e.target.value)}
              placeholder="Ej. Marketing"
              onKeyDown={(e) => {
                if (e.key === 'Enter') createSec();
              }}
            />
            <p className="text-muted-foreground text-xs">
              Después podrás agregar subsecciones y artículos.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewSectionOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={createSec}
              disabled={createSection.isPending || !newSectionTitle.trim()}
            >
              {createSection.isPending && (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              )}
              Crear y abrir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const Route = createFileRoute('/')({ component: Dashboard });
